import { useState, useEffect, useCallback, useRef } from "react";
import { getToken, warmBackend } from './auth';
import { isDemoVisit } from './AccessGate';

export interface NotificationRecord {
  id: number;
  userId: string;
  scheduledAt: string;
  status: string;
  seen: boolean;
  type?: string | null;
  message?: string | null;
  createdAt: string;
}

const API_BASE = '/api';

function authHeaders(): HeadersInit {
  const token = getToken();
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function apiGet<T>(path: string): Promise<T | null> {
  try {
    await warmBackend();
    const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders(), credentials: 'include' });
    if (!res.ok) return null;
    return res.json() as Promise<T>;
  } catch {
    return null;
  }
}

async function apiPost(path: string, body: object): Promise<boolean> {
  try {
    await warmBackend();
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function apiPut(path: string, body?: object): Promise<boolean> {
  try {
    await warmBackend();
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'PUT',
      headers: authHeaders(),
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
    return res.ok;
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const bytes = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    bytes[i] = rawData.charCodeAt(i);
  }
  return bytes.buffer;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [badgeCount, setBadgeCount] = useState(0);
  const [reminderTime, setReminderTimeState] = useState<string>('');
  const [hasSubscription, setHasSubscription] = useState(false);

  // Use a ref so the saveReminderTime callback always reads fresh state
  const hasSubscriptionRef = useRef(hasSubscription);
  hasSubscriptionRef.current = hasSubscription;

  const fetchHistory = useCallback(async () => {
    if (isDemoVisit()) return; // demo visitors have no account to fetch for
    const data = await apiGet<NotificationRecord[]>('/notifications/history');
    if (data) {
      setNotifications(data);
      const unseen = data.filter(n => !n.seen && n.status === 'missed').length;
      setBadgeCount(unseen);
    }
  }, []);

  const fetchPrefs = useCallback(async () => {
    if (isDemoVisit()) return;
    const data = await apiGet<{ reminderTime: string | null; hasSubscription: boolean }>('/notifications/prefs');
    if (data) {
      // Convert stored UTC time back to local for display
      if (data.reminderTime) {
        const [uh, um] = data.reminderTime.split(':').map(Number);
        const d = new Date();
        d.setUTCHours(uh, um, 0, 0);
        setReminderTimeState(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
      } else {
        setReminderTimeState('');
      }
      setHasSubscription(data.hasSubscription);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchPrefs();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // SW registration is best-effort
      });
    }
  }, [fetchHistory, fetchPrefs]);

  const markAllSeen = useCallback(async () => {
    await apiPut('/notifications/mark-seen');
    setBadgeCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, seen: true })));
  }, []);

  const saveReminderTime = useCallback(async (localTime: string): Promise<boolean> => {
    if (!localTime) return false;
    // Convert local HH:MM to UTC HH:MM for the cron worker
    const [h, m] = localTime.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    const time = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return false;

        const registration = await navigator.serviceWorker.ready;
        const vapidData = await apiGet<{ publicKey: string }>('/notifications/vapid-public-key');
        if (!vapidData?.publicKey) return false;

        // Unsubscribe any existing subscription so we always use the current VAPID key
        const existing = await registration.pushManager.getSubscription();
        if (existing) await existing.unsubscribe();

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey),
        });

        const subJson = subscription.toJSON();
        await apiPost('/notifications/subscribe', { subscription: subJson });
        setHasSubscription(true);
      } catch {
        // Push subscription failed — non-critical
      }
    }

    const ok = await apiPut('/notifications/reminder-time', { reminderTime: time });
    if (ok) setReminderTimeState(time);
    return ok;
  }, []);

  return {
    notifications,
    badgeCount,
    reminderTime,
    hasSubscription,
    fetchHistory,
    markAllSeen,
    saveReminderTime,
  };
}
